import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Utils from '../../common/Utils';

const OFFICE_LAT = process.env.REACT_APP_OFFICE_LAT ? parseFloat(process.env.REACT_APP_OFFICE_LAT) : null;
const OFFICE_LNG = process.env.REACT_APP_OFFICE_LNG ? parseFloat(process.env.REACT_APP_OFFICE_LNG) : null;
const OFFICE_RADIUS_M = process.env.REACT_APP_OFFICE_RADIUS_M ? parseInt(process.env.REACT_APP_OFFICE_RADIUS_M) : 200;

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function compressImage(dataUrl, maxWidth = 640) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        } else {
          resolve(dataUrl);
        }
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (err) => reject(err);
    img.src = dataUrl;
  });
}

// attnId: id of today's "mark" record (created via attendence/mark, ontime=1).
// onStartWork: async fn that ensures that record exists (calls attendence/mark
// if needed) — same mark() flow the app already used, just triggered inline
// instead of via a separate "Save Attendance" step.
const CheckInOut = ({ attnId, onCheckin, onCheckout, existingStatus, onStartWork, onOpenDeclaration }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // idle | camera | preview | done
  const [action, setAction] = useState(null); // 'checkin' | 'checkout'
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}

  // Derive state from existingStatus
  const checkedIn = !!(existingStatus && existingStatus.check_in_time);
  const checkedOut = !!(existingStatus && existingStatus.check_out_time);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.error("Autoplay prevented:", err);
          });
        }
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setMsg({ type: 'danger', text: 'Camera permission denied or camera not supported. Cannot proceed.' });
      setPhase('idle');
    }
  }, []);

  useEffect(() => {
    if (phase === 'camera') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [phase, startCamera, stopCamera]);

  const [gpsDetecting, setGpsDetecting] = useState(false);

  const getGps = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ coords: null, error: 'Geolocation is not supported by your browser.' });
        return;
      }

      // Tier 1: Try high accuracy (satellite / hybrid) with 6s timeout & 60s cache
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy },
          error: null,
        }),
        (err) => {
          // If permission is explicitly blocked, do not wait for fallback
          if (err && err.code === 1) {
            resolve({ coords: null, error: 'Location permission denied. Please allow location in browser settings.' });
            return;
          }
          // Tier 2 Fallback: Fast network/WiFi triangulation (enableHighAccuracy: false)
          navigator.geolocation.getCurrentPosition(
            (pos2) => resolve({
              coords: { lat: pos2.coords.latitude, lng: pos2.coords.longitude, accuracy: pos2.coords.accuracy },
              error: null,
            }),
            (err2) => {
              let msg = 'Location not available. Ensure GPS / Location is enabled.';
              if (err2 && err2.code === 1) {
                msg = 'Location permission denied. Please allow location in browser settings.';
              } else if (err2 && err2.code === 3) {
                msg = 'Location request timed out. Please retry.';
              }
              resolve({ coords: null, error: msg });
            },
            { timeout: 8000, enableHighAccuracy: false, maximumAge: 120000 }
          );
        },
        { timeout: 6000, enableHighAccuracy: true, maximumAge: 60000 }
      );
    });

  const retryGps = async () => {
    setGpsDetecting(true);
    const res = await getGps();
    if (res && res.coords) {
      setGps(res.coords);
      setGpsError('');
    } else {
      setGpsError(res?.error || 'Location not available. Ensure GPS / Location is turned on.');
    }
    setGpsDetecting(false);
  };

  const beginAction = async (type) => {
    setAction(type);
    setMsg(null);
    setCapturedImage(null);
    setGps(null);
    setGpsError('');
    setPhase('camera');
    setGpsDetecting(true);
    const res = await getGps();
    if (res && res.coords) {
      setGps(res.coords);
      setGpsError('');
    } else {
      setGpsError(res?.error || 'Location not available. Ensure GPS / Location is turned on.');
    }
    setGpsDetecting(false);
  };

  const handleStartWork = async () => {
    setMsg(null);
    if (!onStartWork) {
      beginAction('checkin');
      return;
    }
    setStarting(true);
    try {
      await onStartWork();
      await beginAction('checkin');
    } catch (err) {
      setMsg({ type: 'danger', text: err?.message || 'Could not start work. Please try again.' });
    } finally {
      setStarting(false);
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }
    const raw = canvas.toDataURL('image/jpeg');
    try {
      const compressed = await compressImage(raw);
      setCapturedImage(compressed);
      setPhase('preview');
    } catch (err) {
      console.error("Image compression error:", err);
      setCapturedImage(raw);
      setPhase('preview');
    } finally {
      stopCamera();
    }
  };

  const getLocationStatus = (coords) => {
    if (!coords) return 'Unknown';
    if (OFFICE_LAT && OFFICE_LNG) {
      const dist = haversineDistance(coords.lat, coords.lng, OFFICE_LAT, OFFICE_LNG);
      return dist <= OFFICE_RADIUS_M ? 'Inside Office' : 'Outside Office';
    }
    return 'Remote';
  };

  const submit = async () => {
    if (!capturedImage) return;
    setLoading(true);
    const locStatus = getLocationStatus(gps);
    const endpoint = action === 'checkin' ? 'attendence/checkin' : 'attendence/checkout';
    const params = new URLSearchParams({
      attn_id: attnId || 0,
      image: capturedImage,
      latitude: gps?.lat ?? '',
      longitude: gps?.lng ?? '',
      address: '',
      location_status: locStatus,
    });
    try {
      const res = await axios.post(Utils.apiUrl(endpoint), params.toString(), Utils.apiHeaders());
      const data = Utils.parseApiResponse(res);
      if (data.success) {
        setMsg({ type: 'success', text: data.message });
        setPhase('idle');
        if (action === 'checkin') {
          onCheckin && onCheckin(data);
        } else {
          onCheckout && onCheckout(data);
        }
      } else {
        setMsg({ type: 'danger', text: data.message });
        setPhase('idle');
      }
    } catch {
      setMsg({ type: 'danger', text: 'Network error. Please try again.' });
      setPhase('idle');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (existingStatus && existingStatus.check_in_time) {
      if (phase === 'done') setPhase('idle');
    }
  }, [existingStatus]);

  const retake = () => {
    setCapturedImage(null);
    setPhase('camera');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (checkedOut) {
    return (
      <div className="text-center py-2">
        <div
          className="mx-auto text-start p-3 shadow-sm bg-white"
          style={{ maxWidth: 320, border: '1.5px solid #CBD5E1', borderRadius: 14 }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
            <span className="fw-bold" style={{ color: "var(--dt-navy)", fontSize: "14px" }}>
              <i className="bi bi-calendar2-check-fill text-success me-2" />Today's Shift
            </span>
            <span className="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-1" style={{ fontSize: "11px" }}>
              COMPLETED
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center small mb-2">
            <span className="text-muted"><i className="bi bi-box-arrow-in-right text-success me-1" />Clock-In</span>
            <strong className="num text-dark">{existingStatus.check_in_time?.slice(11, 16)}</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center small mb-2">
            <span className="text-muted"><i className="bi bi-box-arrow-right text-danger me-1" />Clock-Out</span>
            <strong className="num text-dark">{existingStatus.check_out_time?.slice(11, 16)}</strong>
          </div>
          <div className="d-flex justify-content-between align-items-center small pt-2 border-top">
            <span className="text-muted"><i className="bi bi-hourglass-split text-primary me-1" />Total Duration</span>
            <strong className="num text-primary">{existingStatus.working_hours ? Utils.formatWorkingHours(existingStatus.working_hours) : '—'}</strong>
          </div>
        </div>
        <div className="small text-muted fw-semibold mt-2">
          <i className="bi bi-shield-lock-fill text-primary me-1" />Attendance record locked
        </div>
        <div className="mt-3">
          <Button
            variant="outline-primary"
            size="sm"
            className="fw-bold rounded-pill px-3 py-1 shadow-sm d-inline-flex align-items-center gap-2"
            style={{ fontSize: "12.5px" }}
            onClick={() => onOpenDeclaration && onOpenDeclaration()}
          >
            <i className="bi bi-clock-history text-primary" />
            Declare Extra Hours / Overtime / Adjustments
          </Button>
        </div>
        {(existingStatus.check_out_image || existingStatus.check_in_image) && (
          <div className="mt-3">
            <img
              src={existingStatus.check_out_image || existingStatus.check_in_image}
              alt="Attendance selfie"
              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 12, cursor: 'pointer', border: '2px solid #CBD5E1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
              onClick={() => Utils.viewImage(existingStatus.check_out_image || existingStatus.check_in_image)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Show error/info alerts only – never show when done */}
      {msg && phase !== 'done' && <Alert variant={msg.type} className="py-2 small rounded-3">{msg.text}</Alert>}

      {/* Idle — show check-in or check-out button */}
      {phase === 'idle' && (
        <div className="text-center py-2">
          {!checkedIn && (
            <div className="py-3">
              {starting
                ? <Spinner animation="border" size="sm" variant="primary" />
                : (
                  <Button
                    variant="success"
                    className="fw-bold px-4 py-2 d-inline-flex align-items-center gap-2 shadow-sm"
                    style={{
                      borderRadius: 12,
                      fontSize: 15,
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      border: "none",
                      letterSpacing: "0.02em"
                    }}
                    onClick={handleStartWork}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    START WORK (CLOCK-IN)
                  </Button>
                )
              }
              <div className="text-muted small mt-2">Tap to take a verification selfie and clock-in</div>
            </div>
          )}
          {checkedIn && !checkedOut && (
            <div>
              <div
                className="d-inline-block text-start p-3 mb-3 bg-white shadow-sm"
                style={{ border: '1.5px solid #86EFAC', borderRadius: 14, minWidth: 260 }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold text-success">
                    <i className="bi bi-record-circle-fill me-1" />Shift Active
                  </span>
                  <span className="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-1" style={{ fontSize: "11px" }}>
                    WORKING
                  </span>
                </div>
                <div className="small text-dark mb-1">
                  <i className="bi bi-clock-fill text-success me-2" />
                  Clocked-in at <strong>{existingStatus.check_in_time?.slice(11, 16)}</strong>
                </div>
                <div className="small text-dark mb-1">
                  <i className="bi bi-geo-alt-fill text-primary me-2" />
                  Location: <strong>{existingStatus.location_status || 'Verified'}</strong>
                </div>
                <div className="small text-muted mt-2 pt-1 border-top">
                  <i className="bi bi-camera-fill me-1" />Photo verified
                </div>
              </div>

              {existingStatus.check_in_image && (
                <div className="mb-3">
                  <img
                    src={existingStatus.check_in_image}
                    alt="selfie"
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: '1.5px solid #CBD5E1' }}
                    onClick={() => Utils.viewImage(existingStatus.check_in_image)}
                    title="Click to view full selfie"
                  />
                </div>
              )}
              <div>
                <Button
                  variant="danger"
                  className="fw-bold px-4 py-2 d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{
                    borderRadius: 12,
                    fontSize: 15,
                    background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                    border: "none",
                    letterSpacing: "0.02em"
                  }}
                  onClick={() => beginAction('checkout')}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>
                  FINISH WORK (CLOCK-OUT)
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Camera live view */}
      {(phase === 'camera') && (
        <div className="text-center py-2">
          <div className="fw-bold mb-2 text-dark" style={{ fontSize: "15px" }}>
            {action === 'checkin' ? '📸 Clock-In Verification Selfie' : '📸 Clock-Out Verification Selfie'}
          </div>
          <div className="mx-auto my-2" style={{ maxWidth: 380, width: '100%', padding: '0 8px' }}>
            {gpsDetecting ? (
              <div
                className="d-inline-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded-pill"
                style={{
                  background: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: '1px solid #bae6fd',
                }}
              >
                <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                <span>Detecting GPS Location...</span>
              </div>
            ) : gps ? (
              <div
                className="d-inline-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded-pill"
                style={{
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: '1px solid #c8e6c9',
                }}
              >
                <i className="bi bi-geo-alt-fill text-success" />
                <span>GPS Locked ({gps.lat.toFixed(4)}, {gps.lng.toFixed(4)})</span>
                {gps.accuracy && (
                  <span className="text-muted fw-normal" style={{ fontSize: '10.5px' }}>
                    (±{Math.round(gps.accuracy)}m)
                  </span>
                )}
              </div>
            ) : gpsError ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center gap-1 py-2 px-3 rounded-3 text-center mx-auto"
                style={{
                  background: '#fff8e1',
                  color: '#92400e',
                  fontSize: '11.5px',
                  fontWeight: 500,
                  border: '1px solid #fde68a',
                  maxWidth: 340,
                }}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <i className="bi bi-exclamation-triangle-fill flex-shrink-0 text-warning" />
                  <span style={{ wordBreak: 'break-word', lineHeight: 1.3 }}>{gpsError}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary py-0 px-3 rounded-pill mt-1"
                  style={{ fontSize: '11px', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    retryGps();
                  }}
                >
                  📍 Retry GPS
                </button>
              </div>
            ) : null}
          </div>
          <div className="mx-auto position-relative shadow-sm" style={{ maxWidth: 280, borderRadius: 16, overflow: 'hidden', background: '#000', border: '3px solid #E2E8F0' }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', display: 'block' }} />
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div className="mt-3 d-flex justify-content-center gap-3">
            <Button
              variant="light"
              className="rounded-pill px-3 fw-semibold border"
              onClick={() => { stopCamera(); setPhase('idle'); }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
              onClick={capture}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13" r="3.2"/></svg>
              Capture Selfie
            </Button>
          </div>
        </div>
      )}

      {/* Preview before submit */}
      {phase === 'preview' && capturedImage && (
        <div className="text-center py-2">
          <div className="fw-bold mb-2 text-dark" style={{ fontSize: "14px" }}>
            Verify your selfie before submitting
          </div>
          <div className="position-relative d-inline-block shadow-sm mb-2" style={{ borderRadius: 14, overflow: 'hidden', border: '3px solid #CBD5E1' }}>
            <img src={capturedImage} alt="preview" style={{ width: 150, height: 120, objectFit: 'cover', display: 'block' }} />
          </div>
          <div className="mx-auto my-2" style={{ maxWidth: 380, width: '100%', padding: '0 8px' }}>
            {gpsDetecting ? (
              <div
                className="d-inline-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded-pill"
                style={{
                  background: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: '1px solid #bae6fd',
                }}
              >
                <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                <span>Detecting GPS Location...</span>
              </div>
            ) : gps ? (
              <div
                className="d-inline-flex align-items-center justify-content-center gap-2 py-1 px-3 rounded-pill"
                style={{
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: '1px solid #c8e6c9',
                }}
              >
                <i className="bi bi-geo-alt-fill text-success" />
                <span>GPS Locked ({gps.lat.toFixed(4)}, {gps.lng.toFixed(4)})</span>
                {gps.accuracy && (
                  <span className="text-muted fw-normal" style={{ fontSize: '10.5px' }}>
                    (±{Math.round(gps.accuracy)}m)
                  </span>
                )}
              </div>
            ) : gpsError ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center gap-1 py-2 px-3 rounded-3 text-center mx-auto"
                style={{
                  background: '#fff8e1',
                  color: '#92400e',
                  fontSize: '11.5px',
                  fontWeight: 500,
                  border: '1px solid #fde68a',
                  maxWidth: 340,
                }}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <i className="bi bi-exclamation-triangle-fill flex-shrink-0 text-warning" />
                  <span style={{ wordBreak: 'break-word', lineHeight: 1.3 }}>{gpsError}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary py-0 px-3 rounded-pill mt-1"
                  style={{ fontSize: '11px', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    retryGps();
                  }}
                >
                  📍 Retry GPS
                </button>
              </div>
            ) : null}
          </div>
          <div className="mt-2 d-flex justify-content-center gap-2">
            {loading
              ? <Spinner animation="border" size="sm" variant="primary" />
              : <>
                  <Button
                    variant={action === 'checkin' ? 'success' : 'danger'}
                    className="fw-bold rounded-pill px-4 shadow-sm"
                    onClick={submit}
                  >
                    Confirm &amp; Clock {action === 'checkin' ? 'In' : 'Out'}
                  </Button>
                  <Button variant="outline-secondary" className="rounded-pill px-3" onClick={retake}>
                    Retake
                  </Button>
                </>
            }
          </div>
        </div>
      )}

      {phase === 'done' && msg && (
        <div className="text-center py-3">
          <div
            style={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              background: 'linear-gradient(135deg,#d4edda,#c3e6cb)',
              border: '1px solid #b1dfbb',
              borderRadius: 12,
              padding: '18px 28px',
              boxShadow: '0 4px 16px rgba(40,167,69,0.15)',
            }}
          >
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 28 }} />
            <span style={{ fontWeight: 700, color: '#155724', fontSize: 14 }}>
              {msg.text}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInOut;
